"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import TSLogo from "@/components/global/ui/TSLogo";
import { IFooter } from "@/sanity/lib/definitions";
import { resolvePageLink } from "@/sanity/globals/CTA";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20, filter: "blur(4px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const, delay },
});

export default function Footer({ data }: { data?: IFooter }) {
  if (!data) return null;
  return (
    <footer className="relative z-10 flex w-full flex-col items-center text-white">
      {/* ── Main body ── */}
      <div className="flex w-full max-w-[180rem] flex-col items-start gap-[4rem] px-[3rem] py-[7rem] lg:flex-row lg:justify-between lg:gap-0 lg:px-0 lg:pb-[8rem]">
        {/* Nav columns */}
        <div className="flex w-full gap-[4rem] lg:w-fit lg:gap-[7.375rem]">
          {(data.navColumns ?? []).map((col, i) => (
            <motion.div
              key={i}
              {...fadeUp(i * 0.1)}
              className="flex min-w-0 flex-1 flex-col gap-[1rem] lg:w-[16.125rem] lg:flex-none">
              <span className="font-spacemono text-[1.75rem] leading-[1.4] tracking-[0.05rem] uppercase">
                {col.heading}
              </span>
              {col.links.map((item, j) => {
                const href = resolvePageLink(item.link) ?? "#";
                const isExternal = item.link?.externalLink && item.link?.openInNewTab;
                return (
                  <Link
                    key={j}
                    href={href}
                    target={isExternal ? "_blank" : "_self"}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                    className="font-helveticaNeue text-[2rem] leading-[1.4] whitespace-nowrap text-white transition-colors duration-200 hover:text-white/60">
                    {item.title}
                  </Link>
                );
              })}
            </motion.div>
          ))}
        </div>

        {/* Powered by card */}
        <motion.div
          {...fadeUp(0.2)}
          className="flex h-fit w-full items-center gap-[3rem] rounded-[2rem] p-[1rem] backdrop-blur-[1.8rem] lg:w-auto"
          style={{ background: "rgba(0,0,0,0.2)" }}>
          {/* Card badge */}
          <div
            className="relative flex h-[13rem] w-[9.75rem] flex-shrink-0 flex-col justify-between rounded-[1rem] p-[1rem]"
            style={{
              background: "linear-gradient(to bottom, #ffffff, #dedede)",
              border: "0.125rem solid rgba(255,255,255,0.2)",
            }}>
            <div className="flex justify-between">
              <span className="h-[0.75rem] w-[0.75rem] bg-[#030A19]" />
              <span className="h-[0.75rem] w-[0.75rem] bg-[#030A19]" />
            </div>
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-helveticaNeue text-[3rem] leading-[1.4] whitespace-nowrap text-[#030A19]">
              {data.poweredByCard && data.poweredByCard.cardLabel}
            </span>
            <div className="flex justify-between">
              <span className="h-[0.75rem] w-[0.75rem] bg-[#030A19]" />
              <span className="h-[0.75rem] w-[0.75rem] bg-[#030A19]" />
            </div>
          </div>

          {/* Text block */}
          <div className="flex min-w-0 flex-1 flex-col gap-[4rem] lg:w-[34rem] lg:flex-none">
            <p className="mb-0 font-spacemono text-[2rem] leading-[1.4] text-white uppercase">
              {data.poweredByCard && data.poweredByCard.headline}
            </p>
            <p className="mb-0 font-spacemono text-[2rem] leading-[1.4] text-white/60 uppercase">
              {data.poweredByCard && data.poweredByCard.subtext}
            </p>
          </div>
        </motion.div>
      </div>

      {/* ── Bottom bar ── */}
      {/* No negative margin: when the footer ends the page, the shifted bar
          never gets 40px inside the viewport and stays hidden. */}
      <motion.div
        {...fadeUp(0.1)}
        viewport={{ once: true }}
        className="relative flex w-full flex-col items-center gap-[1rem] p-[3rem] lg:flex-row lg:justify-between lg:gap-0 lg:px-[max(3rem,calc((100%_-_180rem)/2))]">
        {/* Copyright */}
        <span className="order-2 font-spacemono text-[2rem] leading-[1.4] whitespace-nowrap uppercase lg:order-1">
          {data.copyright}
        </span>

        {/* Logo centered */}
        <div className="order-1 lg:absolute lg:top-1/2 lg:left-1/2 lg:order-2 lg:-translate-x-1/2 lg:-translate-y-1/2">
          <TSLogo
            width="4.375rem"
            height="5rem"
          />
        </div>

        {/* Legal links */}
        <div className="order-3 flex flex-wrap items-center justify-center gap-x-[4rem] gap-y-[1rem]">
          {(data.legalLinks ?? []).map((item, i) => {
            const href = resolvePageLink(item.link) ?? "#";
            return (
              <Link
                key={i}
                href={href}
                className="font-spacemono text-[2rem] leading-[1.4] whitespace-nowrap uppercase transition-colors duration-200 hover:text-white/60">
                {item.title}
              </Link>
            );
          })}
        </div>
      </motion.div>
    </footer>
  );
}
